'use client';

import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, type ReactNode } from 'react';
import { ApiProvider } from '@/contexts/ApiContext';
import { createQueryClient } from '@/lib/queryClient';
import { detectAndApplyBrowserLanguage } from '@/stores/languageStore';
import { useAuthStoreHydrate } from '@/stores/authStore';
import { WebUrlNavigationProvider } from '@/components/WebUrlNavigationProvider';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';
import { useYandexMetrika } from '@/hooks/useYandexMetrika';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';
import { prefetchLoadingAssets } from '@/lib/prefetchLoadingAssets';
import { initAudioSession } from '@/lib/audioSession';
import { initClickSound, preloadUiSounds } from '@/lib/uiSounds';

export function Providers({ children }: { children: ReactNode }) {
  const queryClient = useMemo(() => createQueryClient(), []);
  useAuthStoreHydrate();
  useYandexMetrika();
  useFacebookPixel();

  useEffect(() => {
    detectAndApplyBrowserLanguage();
    prefetchLoadingAssets();
    initAudioSession();
    preloadUiSounds();
    initClickSound();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider>

        <WebUrlNavigationProvider>
          {children}

          <ConfirmDialogHost />
        </WebUrlNavigationProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
